import { Box, ListItemIcon, Menu, MenuItem } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { UserAccessDetails } from '../../../../../../api/user/interfaces'
import { IS_INTEGRATED_VERSION } from '../../../../../../config/ConfigManager'
import { redirectToAllianceConnect } from '../../../../../../helpers/redirectToAllianceConnect'
import { I18n, StorageKeys, languageOptions } from 'common'
import UserInfoServiceProvider from '../../../../../../services/UserInfoServiceProvider'
import { useSearchStore } from '../../../../../../store/search/SearchStore'
import { defaultTheme } from '../../../../../../theme/index'
import { breakpoints } from '../../../../../../theme/breakpoints'
import styled from 'styled-components'
import authManager from '../../../../../../api/security/Auth'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ISelectedOption } from '../../../../../search/AllProductsSearch/LanguagesDropdown'
import { useUserStore } from '../../../../../../store/user/UserStore'
import AttributesServiceProvider from '../../../../../../services/AttributesServiceProvider'
import {
  backupSession,
  getCurrentSession,
  hasBackedUpSession,
  restoreBackedUpSession,
} from '../../../../../login/helper'

const isImpersonating =
  localStorage.getItem(StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY) === 'true'
const isSalespad = localStorage.getItem(StorageKeys.SALESPAD_KEY) === 'true'

interface AccountMenuProps {
  open: boolean
  parentRef: React.RefObject<HTMLElement>
  onClose: () => void
  accountName: string
}

interface ILangMenuProps {
  handleClose: (value: string) => void
  anchorEl: HTMLElement
}

const LangMenu = ({ anchorEl, handleClose }: ILangMenuProps) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      {languageOptions.map((option) => (
        <LangMenuItem
          onClick={() => {
            handleClose(option.key)
          }}
          key={option.key}
        >
          <LangListIcon>
            <FlagImg src={option.imgPath} alt={option.label} />
          </LangListIcon>
          <Label className="pl-3">
            <I18n name={option.label}></I18n>
          </Label>
        </LangMenuItem>
      ))}
    </Menu>
  )
}

export const ProfileMenu = ({
  open,
  parentRef,
  onClose,
  accountName,
}: AccountMenuProps): ReactElement => {
  const user = useUserStore()
  const searchStore = useSearchStore()
  const [userAccessDetails, setUserAccessDetails] =
    useState<UserAccessDetails>(undefined)
  const [openLnagMenu, setOpenLnagMenu] = useState<HTMLElement | null>(null)
  const [language, setLanguage] = useState<ISelectedOption>(
    languageOptions.find((item) => item.key === user.preferences.language) ||
      languageOptions[0]
  )

  const redirectUser = (dashboard): void => {
    redirectToAllianceConnect(userAccessDetails.url, dashboard)
  }

  const redirectUserToLoginWidget = (): void => {
    const originToRedirect = localStorage.getItem(
      StorageKeys.AUTO_LOGIN_ORIGIN_KEY
    )
    if (originToRedirect) {
      window.location.href = `${originToRedirect}/?salespad=true`
    }
  }

  const redirectToProPortal = (url: string): void => {
    window.open(url, '_blank')
  }

  useEffect(() => {
    if (open && !IS_INTEGRATED_VERSION && !isSalespad && !isImpersonating) {
      UserInfoServiceProvider.getUserAccess(authManager.getUserId()).then(
        (data) => {
          setUserAccessDetails(data)
        }
      )
    }
  }, [open])

  const onChangeLanguage = (selectedLanguage: ISelectedOption) => {
    const reqData = [
      {
        uuid: 'DEFAULT_LANGUAGE',
        domId: Number(user.preferences.userId),
        domType: 5,
        attrValue: selectedLanguage.key,
      },
    ]

    AttributesServiceProvider.saveOrUpdateAttribute(reqData)
    setLanguage(selectedLanguage)
  }

  const handleMenuClose = (value: string) => {
    setOpenLnagMenu(null)
    const selectedLanguage = languageOptions.find((lang) => lang.key === value)

    // Trigger only if a valid language is found
    if (selectedLanguage) {
      onChangeLanguage(selectedLanguage)
    }
  }

  const handleSubmenuOpen = (event) => {
    setOpenLnagMenu(event.currentTarget)
  }

  const handleSwitchOrganization = () => {
    onClose()
    const session = getCurrentSession()
    if (hasBackedUpSession()) {
      restoreBackedUpSession()
      backupSession(session)
      window.location.reload()
    } else {
      redirectUserToLoginWidget()
    }
  }

  return (
    <StyledMenu
      open={open}
      onClose={onClose}
      anchorEl={parentRef?.current}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <AccountMenuItem onClick={onClose}>
        <span className="material-icons" style={iconStyle}>
          account_circle
        </span>
        {accountName}
      </AccountMenuItem>

      <AccountMenuItem aria-haspopup="true" onClick={handleSubmenuOpen}>
        <AccountListItemIcon>
          <FlagImg src={language.imgPath} alt={language.label} />
        </AccountListItemIcon>
        <Label className="pl-3">
          <I18n name={language.label}></I18n>
        </Label>
        <ChevronRightIcon />
      </AccountMenuItem>
      <LangMenu anchorEl={openLnagMenu} handleClose={handleMenuClose} />

      {!IS_INTEGRATED_VERSION && !isSalespad && !isImpersonating && (
        // https://stackoverflow.com/a/78630639
        <Box>
          {userAccessDetails?.isAccess && (
            <AccountMenuItem
              onClick={() => {
                onClose()
                redirectUser(false)
              }}
            >
              <BoxWrapper />
              <I18n name="allianceConnect" />
            </AccountMenuItem>
          )}

          <AccountMenuItem
            onClick={() => {
              onClose()
              redirectUser(true)
            }}
          >
            <DashboardWrapper />
            <I18n name="dashboard" />
          </AccountMenuItem>
          {userAccessDetails?.proURLs &&
            userAccessDetails.proURLs.length > 0 &&
            userAccessDetails.proURLs.map((pro) => (
              <AccountMenuItem
                key={pro.proURL}
                onClick={() => {
                  onClose()
                  redirectToProPortal(pro.proURL)
                }}
              >
                <ProPortalIcon />
                {pro.proURLName}
              </AccountMenuItem>
            ))}
        </Box>
      )}

      {isImpersonating && isSalespad && (
        <AccountMenuItem onClick={handleSwitchOrganization}>
          <span className="material-icons" style={iconStyle}>
            compare_arrows
          </span>
          <I18n name="switchOrg" />
        </AccountMenuItem>
      )}

      {/* AES doesn't have login and logout */}
      {!IS_INTEGRATED_VERSION && (
        <AccountMenuItem
          onClick={() => {
            onClose()
            user.signOut()
            searchStore.removeBreadCrumbs()
          }}
        >
          <span className="material-icons" style={iconStyle}>
            exit_to_app
          </span>
          <I18n name="logOut" />
        </AccountMenuItem>
      )}
    </StyledMenu>
  )
}

const StyledMenu = styled(Menu)`
  margin-top: 15px;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    margin-top: 12px;
  }
`
const BoxWrapper = styled.div`
  background-color: ${defaultTheme.colors.orange};
  width: 18px;
  height: 18px;
  border: 4px solid rgba(0, 0, 0, 1);
  margin-right: 18px;
  margin-left: 2px;
`

const DashboardWrapper = styled.div`
  background-color: ${defaultTheme.colors.blueLight};
  width: 18px;
  height: 18px;
  border: 4px solid rgba(0, 0, 0, 1);
  margin-right: 18px;
  margin-left: 2px;
`

const ProPortalIcon = styled.div`
  background-color: ${defaultTheme.colors.yellowLight};
  width: 18px;
  height: 18px;
  border: 4px solid rgba(0, 0, 0, 1);
  margin-right: 18px;
  margin-left: 2px;
`

const iconStyle = {
  width: 24,
  height: 24,
  marginRight: 14,
}

const FlagImg = styled('img')`
  width: 20px;
`
const Label = styled('span')`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  text-transform: uppercase;
  color: #050505;
  width: 100%;
`
const LangMenuItem = styled(MenuItem)`
  width: 124px;
  height: 40px;
  padding: 8px 20px 8px 12px;
`
const AccountMenuItem = styled(MenuItem)`
  width: 247px;
  height: 40px;
  padding: 8px 16px 8px 16px;
`
const LangListIcon = styled(MenuItem)`
  min-width: 30px;
  padding: 0px;
`
const AccountListItemIcon = styled(ListItemIcon)`
  min-width: 34px !important;
`
